// src/pages/PagodaStackerGamePage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Home } from 'lucide-react';

const PagodaStackerGamePage = () => {
    // Game Configuration
    const INITIAL_WIDTH = 200;
    const BLOCK_HEIGHT = 40;
    const MOVE_SPEED = 3;
    const GAME_AREA_HEIGHT = 500;

    // State
    const [stack, setStack] = useState([{ x: 0, width: INITIAL_WIDTH }]);
    const [currentBlock, setCurrentBlock] = useState({ x: 0, direction: 1 });
    const [score, setScore] = useState(0);
    const [gameOver, setGameOver] = useState(false);
    const [gameStarted, setGameStarted] = useState(false);

    const requestRef = useRef();

    // เริ่มเกม
    const startGame = () => {
        setStack([{ x: 0, width: INITIAL_WIDTH }]);
        setCurrentBlock({ x: -150, direction: 1 });
        setScore(0);
        setGameOver(false);
        setGameStarted(true);
    };

    // Game Loop
    const animate = () => {
        if (gameOver || !gameStarted) return;

        setCurrentBlock(prev => {
            let newX = prev.x + (MOVE_SPEED * prev.direction + (score * 0.1 * prev.direction));

            if (newX > 150) return { ...prev, x: 150, direction: -1 };
            if (newX < -150) return { ...prev, x: -150, direction: 1 };

            return { ...prev, x: newX };
        });

        requestRef.current = requestAnimationFrame(animate);
    };

    useEffect(() => {
        if (gameStarted && !gameOver) {
            requestRef.current = requestAnimationFrame(animate);
        }
        return () => cancelAnimationFrame(requestRef.current);
    }, [gameStarted, gameOver, score]);

    // ฟังก์ชันวางชั้น (แก้ไขให้รับ event และป้องกันการกดเบิ้ล)
    const placeBlock = (e) => {
        // ป้องกัน Default Action (เช่น การ Zoom หรือ Scroll หรือการส่ง Event ซ้ำบนบาง Browser)
        if (e && e.cancelable) {
             e.preventDefault();
        }

        if (!gameStarted || gameOver) return;

        const lastBlock = stack[stack.length - 1];
        const currentX = currentBlock.x;

        const distance = currentX - lastBlock.x;
        const overlap = lastBlock.width - Math.abs(distance);

        if (overlap > 0) {
            const newWidth = overlap;
            const newX = lastBlock.x + (distance / 2);

            setStack(prev => [...prev, { x: newX, width: newWidth }]);
            setScore(prev => prev + 1);

            setCurrentBlock({ x: -150, direction: 1 });
        } else {
            setGameOver(true);
        }
    };

    return (
        <div 
            className="min-h-screen bg-gradient-to-b from-sky-300 to-sky-100 flex flex-col items-center justify-center font-sans overflow-hidden select-none touch-none"
            // --- จุดที่แก้ไข: ใช้ onPointerDown แทน onMouseDown/onTouchStart ---
            onPointerDown={placeBlock} 
        >
            <div className="absolute top-4 left-4 z-50 flex gap-2">
                <Link
                    to="/games"
                    className="flex items-center justify-center w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full shadow-md text-amber-800 hover:bg-white hover:scale-110 transition-all border border-amber-100"
                    title="กลับไปหน้ารวมเกม"
                    // ป้องกัน event ทะลุลงไปที่พื้นหลังเวลาดกดปุ่ม
                    onPointerDown={(e) => e.stopPropagation()} 
                >
                    <ChevronLeft className="w-6 h-6" />
                </Link>

                <Link
                    to="/"
                    className="flex items-center justify-center w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full shadow-md text-amber-800 hover:bg-white hover:scale-110 transition-all border border-amber-100"
                    title="กลับหน้าหลักเว็บไซต์"
                    // ป้องกัน event ทะลุลงไปที่พื้นหลังเวลาดกดปุ่ม
                    onPointerDown={(e) => e.stopPropagation()}
                >
                    <Home className="w-5 h-5" />
                </Link>
            </div>
            
            {/* Background Decor */}
            <div className="absolute bottom-0 w-full opacity-30 pointer-events-none">
                <svg viewBox="0 0 1440 320" className="w-full">
                    <path fill="#0099ff" fillOpacity="0.5" d="M0,224L60,213.3C120,203,240,181,360,186.7C480,192,600,224,720,218.7C840,213,960,171,1080,160C1200,149,1320,171,1380,181.3L1440,192L1440,320L1380,320C1320,320,1200,320,1080,320C960,320,840,320,720,320C600,320,480,320,360,320C240,320,120,320,60,320L0,320Z"></path>
                </svg>
            </div>

            <div className="z-10 text-center mb-4">
                <h1 className="text-3xl font-bold text-amber-800 drop-shadow-md">สร้างพระธาตุ</h1>
                <p className="text-amber-700 font-bold text-xl">ชั้นที่: {score}</p>
            </div>

            {/* Game Area */}
            <div
                className="relative w-full max-w-md bg-white/20 backdrop-blur-sm border-2 border-white/50 rounded-xl overflow-hidden shadow-2xl"
                style={{ height: `${GAME_AREA_HEIGHT}px` }}
            >
                <div className="absolute bottom-0 left-1/2 w-full h-full transform -translate-x-1/2 flex flex-col-reverse items-center pb-10 transition-transform duration-500"
                    style={{ transform: `translate(-50%, ${Math.max(0, (stack.length * BLOCK_HEIGHT) - (GAME_AREA_HEIGHT / 2))}px)` }}
                >
                    {/* Base & Stacked Blocks */}
                    {stack.map((block, i) => (
                        <div
                            key={i}
                            className="relative transition-all duration-300"
                            style={{
                                width: `${block.width}px`,
                                height: `${BLOCK_HEIGHT}px`,
                                transform: `translateX(${block.x}px)`,
                                backgroundColor: i === 0 ? '#8B4513' : '#FFD700',
                                boxShadow: 'inset 0 -5px 0 rgba(0,0,0,0.1), 0 4px 6px rgba(0,0,0,0.1)',
                                borderTop: '2px solid #FFF8DC',
                                borderRadius: i === 0 ? '4px' : '2px'
                            }}
                        >
                            {i > 0 && (
                                <div className="w-full h-full flex justify-center items-center opacity-50">
                                    <div className="w-[80%] h-[50%] border-x-2 border-amber-600"></div>
                                </div>
                            )}
                        </div>
                    ))}

                    {/* Current Moving Block */}
                    {!gameOver && gameStarted && (
                        <div
                            className="absolute bg-amber-400"
                            style={{
                                width: `${stack[stack.length - 1].width}px`,
                                height: `${BLOCK_HEIGHT}px`,
                                bottom: `${stack.length * BLOCK_HEIGHT + 40}px`,
                                transform: `translateX(${currentBlock.x}px)`,
                                boxShadow: '0 10px 20px rgba(0,0,0,0.2)',
                                borderRadius: '2px',
                                borderTop: '2px solid #FFF'
                            }}
                        >
                            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 text-amber-600">▲</div>
                        </div>
                    )}
                </div>

                <div className="absolute bottom-0 w-full h-10 bg-green-600 border-t-4 border-green-700"></div>
            </div>

            <p className="mt-4 text-gray-600 animate-pulse text-sm">
                {gameStarted && !gameOver ? "แตะเพื่อวางชั้นเจดีย์" : ""}
            </p>

            {/* Start / Game Over Modal */}
            {(!gameStarted || gameOver) && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
                    <div 
                        className="bg-white p-8 rounded-2xl shadow-2xl text-center max-w-sm animate-bounce-in border-4 border-amber-200"
                        // ป้องกัน event ทะลุเวลาคลิกที่กล่อง Modal
                        onPointerDown={(e) => e.stopPropagation()}
                    >
                        {gameOver ? (
                            <>
                                <h2 className="text-2xl font-bold text-red-500 mb-2">พระธาตุถล่ม!</h2>
                                <p className="text-gray-600 mb-4">คุณสร้างได้สูงถึง {score} ชั้น</p>
                            </>
                        ) : (
                            <>
                                <h2 className="text-2xl font-bold text-amber-600 mb-2">สร้างพระธาตุเสียดฟ้า</h2>
                                <p className="text-gray-500 mb-4">แตะหน้าจอให้ตรงจังหวะ<br />เพื่อเรียงชั้นเจดีย์ให้สูงที่สุด</p>
                            </>
                        )}

                        <button
                            onClick={startGame}
                            className="bg-amber-500 hover:bg-amber-600 text-white font-bold py-3 px-8 rounded-full shadow-lg transform transition active:scale-95"
                        >
                            {gameOver ? 'สร้างใหม่' : 'เริ่มเกม'}
                        </button>
                    </div>
                </div>
            )}

            {gameOver && (
                <div className="absolute bottom-6 bg-white/90 p-3 rounded-xl shadow-lg max-w-xs text-xs text-center text-amber-900 border border-amber-200">
                    💡 <strong>รู้หรือไม่:</strong> พระธาตุดอยสุเทพเป็นเจดีย์ทรงเชียงแสน ก่ออิฐถือปูนปิดทอง เป็นศิลปะล้านนาที่งดงามที่สุดแห่งหนึ่ง
                </div>
            )}
        </div>
    );
};

export default PagodaStackerGamePage;